import React, { useState, useEffect, useCallback } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl-next';

import { RateLimitedError, BadStatusError } from '@confluence/network';
import type { NetworkError } from '@confluence/network';

import {
	getUnsplashPhotos,
	isUnsplashImage,
	type UnsplashResult,
} from '../UnsplashSearch/unsplashAPI';
import {
	CARDS_DEFAULT_UNSPLASH_QUERY_WORD,
	PAGE_TITLE_DEFAULT_UNSPLASH_QUERY_WORD,
} from '../UnsplashSearch/UnsplashSearchContent';

const i18n = defineMessages({
	unsplashApiNoNetworkErrorMessage: {
		id: 'custom-sites-extensions.unsplash.search.error.no-network-message',
		// TODO: Fix the direction of the curly quote below. (see go/curlyquotes)
		// eslint-disable-next-line no-restricted-syntax
		defaultMessage: 'We‘ve lost the connection. Try again.',
		description:
			'Message for error shown to user when an api request made to Unsplash fails because the network appeared to be offline',
	},
	unsplashApiBadStatusErrorMessage: {
		id: 'custom-sites-extensions.unsplash.error.bad-request-message',
		// TODO: Fix the direction of the curly quote below. (see go/curlyquotes)
		// eslint-disable-next-line no-restricted-syntax
		defaultMessage: 'We can‘t get a response from Unsplash. Refresh and try again.',
		description:
			'Message for error shown to user when an api request made to Unsplash fails because the request was unsuccessful for reasons other than network or rate limit',
	},
	unsplashApiRateLimitedErrorMessage: {
		id: 'custom-sites-extensions.unsplash.error.rate-limited-message',
		// TODO: Fix the direction of the curly quote below. (see go/curlyquotes)
		// eslint-disable-next-line no-restricted-syntax
		defaultMessage: 'We can‘t get a response from Unsplash. Wait a moment, then try again.',
		description:
			'Message for error shown to user when an api request made to Unsplash fails because too many requests were sent to Unsplash in a given period',
	},
});

export enum UnsplashFetchError {
	NO_NETWORK_ERROR = 'network_error',
	RATE_LIMITED_ERROR = 'rate_limited_error',
	BAD_STATUS_ERROR = 'bad_status_error',
}

const i18nErrorText = {
	[UnsplashFetchError.NO_NETWORK_ERROR]: (
		<FormattedMessage {...i18n.unsplashApiNoNetworkErrorMessage} />
	),
	[UnsplashFetchError.BAD_STATUS_ERROR]: (
		<FormattedMessage {...i18n.unsplashApiBadStatusErrorMessage} />
	),
	[UnsplashFetchError.RATE_LIMITED_ERROR]: (
		<FormattedMessage {...i18n.unsplashApiRateLimitedErrorMessage} />
	),
};

export const useUnsplash = ({ isPageTitleComponent, selectedImageUrl, initialUnsplashResults }) => {
	const [defaultUnsplashResults, setDefaultUnsplashResults] = useState<
		UnsplashResult[] | undefined
	>([]);
	const [unSplashSearchResults, setUnsplashSearchResults] = useState<UnsplashResult[] | undefined>(
		[],
	);
	const [fetchError, setError] = useState<UnsplashFetchError | undefined>();
	const contentTopperDefaultImageUrl = isUnsplashImage(selectedImageUrl) ? selectedImageUrl : '';
	const selectedDefaultImageUrl = !isPageTitleComponent
		? selectedImageUrl
		: contentTopperDefaultImageUrl;

	const handleError = useCallback(
		(error: NetworkError) => {
			if (error instanceof RateLimitedError) {
				setError(UnsplashFetchError.RATE_LIMITED_ERROR);
			} else if (error instanceof BadStatusError) {
				setError(UnsplashFetchError.BAD_STATUS_ERROR);
			} else {
				setError(UnsplashFetchError.NO_NETWORK_ERROR);
			}
			setUnsplashSearchResults(undefined);
		},
		[setError, setUnsplashSearchResults],
	);

	useEffect(() => {
		if (!initialUnsplashResults) {
			const defaultUnsplashQueryWord = isPageTitleComponent
				? PAGE_TITLE_DEFAULT_UNSPLASH_QUERY_WORD
				: CARDS_DEFAULT_UNSPLASH_QUERY_WORD;
			void getUnsplashPhotos(defaultUnsplashQueryWord, 1, isPageTitleComponent)
				.then((result) => {
					setUnsplashSearchResults(result);
					setDefaultUnsplashResults(result);
				})
				.catch((responseError) => {
					handleError(responseError);
				});
		} else {
			setUnsplashSearchResults(initialUnsplashResults);
			setDefaultUnsplashResults(initialUnsplashResults);
		}
	}, [initialUnsplashResults, isPageTitleComponent, handleError]);

	return {
		selectedDefaultImageUrl,
		defaultUnsplashResults,
		unSplashSearchResults,
		errorMessage: fetchError ? i18nErrorText[fetchError] : undefined,
		handleError,
		setUnsplashSearchResults,
		setDefaultUnsplashResults,
		setError,
	};
};
